import React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { TidewaysContext, ContextRoute } from './app/context'
import { Entity } from './app/entity'
import { Trace, traceSpanCategories } from './domain/Trace'
import Link from './helper/link'
import Loading from './helper/loading'
import { PortalContentNavigationPublic } from './helper/portal-content-navigation-public'
import { parseQueryString } from './helper/queryString'
import { ApplicationState } from './redux/ApplicationState'
import { TraceEntity } from './redux/traces/TracesState'
import { getTrace, isTraceLoaded } from './redux/V2/Trace/TraceState'
import Router from './router'
import Bottlenecks from './trace/bottlenecks'
import { Callgraph } from './trace/callgraph'
import NoCallgraphAttached from './trace/no-callgraph-attached'
import TraceOverview from './trace/overview'
import RelatedTraces from './trace/related-traces'
import Timeline from './trace/timeline'
import { Container } from './ui/grid'
import SpanBasedSummary from './trace/span-based-summary'
import { FlamegraphComponent } from './trace/flamegraph'
import { isAggregatedTrace } from './domain/TraceSummary'

interface MatchParameters {
    profile: string
}

interface OwnProps {
    view?: 'timeline' | 'flamegraph' | 'callgraph' | 'summary' | 'bottlenecks' | 'compare' | 'related-traces'
    traceId: string
}

type StateProps = {
    tidewaysContext: TidewaysContext
    router: Router
    route: ContextRoute
    trace: Trace | null
    traceId: string
    traceLoaded?: boolean
} & Entity<TraceEntity | null>

type Props = OwnProps & StateProps & RouteComponentProps<MatchParameters>

export class PublicTraceComponent extends React.Component<Props> {
    public componentDidMount() {
        this.setRoutingDefaultParameters()
    }

    public findTracepointEvent() {
        // TODO: why do we have this.props.data.trace and this.props.trace?
        if (
            !this.props.data ||
            !this.props.data.traces.length ||
            !this.props.data.traces[0].globalEvents ||
            !this.props.trace ||
            !this.props.trace.xhprofReport ||
            !this.props.trace.xhprofReport.overallTotals
        ) {
            return null
        }

        const tracePointEvents = this.props.data.traces[0].globalEvents.filter(event => event.name === 'tracepoint-start')

        if (tracePointEvents.length === 1) {
            const startTime = (tracePointEvents[0].ts / 1000).toFixed(0)

            if (parseInt(startTime, 10) === 0) {
                // "tracepoint-start" event happens so early, don't even show the event
                return null
            }

            return {
                duration: (this.props.trace.xhprofReport.overallTotals.wt / 1000).toFixed(0),
                startTime,
            }
        }

        return null
    }

    public render() {
        const { router } = this.props
        let { view: active = 'timeline' } = this.props

        if (!this.props.data || !this.props.data.profile || !this.props.data.traces) {
            return <Loading />
        }

        const rootTrace = this.props.data.traces[0]

        // aggregated traces do not have proper spans, therefore default to flamegraph
        if (active === 'timeline' && isAggregatedTrace(this.props.data.profile)) {
            active = 'flamegraph'
        }

        const startTracepointEvent = this.findTracepointEvent()

        return (
            <div>
                <PortalContentNavigationPublic>
                    <div style={{ background: 'white' }}>
                        <Container>
                            <TraceOverview
                                profile={this.props.data.profile}
                                bottlenecks={this.props.data.bottlenecks}
                                bottleneckTypes={this.props.data.bottleneckTypes}
                                compact
                                operation={this.props.data.operation}
                                route="XhprofCoreBundle.Public.sharedTrace.quick"
                                isSharedTraceView={true}
                            />
                        </Container>
                    </div>
                </PortalContentNavigationPublic>

                <div className="row">
                    <div className="col-md-12">
                        <ul className="nav nav-pills nav-pills-tideways">
                            {isAggregatedTrace(this.props.data.profile) ? null : (
                                <li className={active === 'timeline' ? 'active' : ''}>
                                    <Link route="XhprofCoreBundle.Public.sharedTrace.quick">Timeline</Link>
                                </li>
                            )}
                            <li className={active === 'flamegraph' ? 'active' : ''}>
                                <Link route="XhprofCoreBundle.Public.sharedTrace.flamegraph">Flamegraph</Link>
                            </li>

                            <li className={active === 'summary' ? 'active' : ''}>
                                <Link route="XhprofCoreBundle.Public.sharedTrace.summary">Summary</Link>
                            </li>
                            <li className={active === 'callgraph' ? 'active' : ''}>
                                <Link route="XhprofCoreBundle.Public.sharedTrace.callgraph">Callgraph</Link>
                            </li>
                            {this.props.data.hasBottlenecks ? (
                                <li className={active === 'bottlenecks' ? 'active' : ''}>
                                    <Link route="XhprofCoreBundle.Public.sharedTrace.bottlenecks">
                                        Bottlenecks <span className="label label-danger">{this.props.data.bottlenecksReport.length}</span>
                                    </Link>
                                </li>
                            ) : null}
                            {this.hasTraceHierachyWithChildren() ? (
                                <li className={active === 'related-traces' ? 'active' : ''}>
                                    <Link route="XhprofCoreBundle.Public.sharedTrace.quick">Related</Link>
                                </li>
                            ) : null}
                        </ul>
                    </div>
                </div>
                {(() => {
                    const queryParameters = {
                        ...parseQueryString(this.props.location.search),
                        shareId: this.props.tidewaysContext.route.parameters.shareId || 'unknown',
                    }
                    switch (active) {
                        case 'timeline':
                            return (
                                <Timeline
                                    categories={traceSpanCategories}
                                    traces={this.props.data.traces}
                                    router={router}
                                    route="XhprofCoreBundle.Public.sharedTrace.quick"
                                    hasCallgraph={this.props.data.hasCallgraph}
                                    hasMemory={this.props.data.hasMemory}
                                    params={queryParameters}
                                    layerReport={this.props.data.profile.layers}
                                    maxSpanLimit={1500}
                                    publicVersion
                                />
                            )
                        case 'flamegraph':
                            return (
                                <FlamegraphComponent
                                    spans={rootTrace.spans}
                                    data={rootTrace.flamegraph}
                                    categories={traceSpanCategories}
                                    router={router}
                                    route="XhprofCoreBundle.Public.sharedTrace.flamegraph"
                                    params={queryParameters}
                                    isAggregatedTrace={isAggregatedTrace(this.props.data.profile)}
                                    publicVersion
                                    layerReport={this.props.data.profile.layers}
                                />
                            )
                        case 'summary':
                            return (
                                <SpanBasedSummary
                                    spans={rootTrace.spans}
                                    categories={traceSpanCategories}
                                    router={router}
                                    params={queryParameters}
                                    route="XhprofCoreBundle.Public.sharedTrace.summary"
                                    publicVersion
                                    layerReport={this.props.data.profile.layers}
                                />
                            )
                        case 'bottlenecks':
                            return (
                                <Bottlenecks
                                    messages={this.props.data?.bottlenecksReport}
                                    spans={rootTrace.spans}
                                    router={router}
                                    params={queryParameters}
                                    routes={{
                                        clickRoute: 'XhprofCoreBundle.Public.sharedTrace.bottlenecks',
                                        callgraphRoute: 'XhprofCoreBundle.Public.sharedTrace.callgraph',
                                        timelineRoute: 'XhprofCoreBundle.Public.sharedTrace.quick',
                                    }}
                                />
                            )
                        case 'callgraph':
                            return this.props.data.hasCallgraph && this.props.trace ? (
                                <Callgraph
                                    trace={this.props.trace}
                                    router={router}
                                    url={router.path('XhprofCoreBundle.Public.sharedTrace.callgraph', this.props.match.params)}
                                    clickRoute="XhprofCoreBundle.Public.sharedTrace.callgraph"
                                    params={queryParameters}
                                    startTracepointEvent={startTracepointEvent}
                                    showPlusWhenPositive={false}
                                    includesDynamicallyInstrumentedFunctions={false}
                                    bottlenecks={this.props.data.bottlenecksReport}
                                    bottleneckRoute="XhprofCoreBundle.Public.sharedTrace.bottlenecks"
                                />
                            ) : (
                                <NoCallgraphAttached profile={this.props.data.profile} />
                            )
                        case 'related-traces':
                            return <RelatedTraces traceHierarchy={this.props.data?.traceHierarchy} currentTraceId={this.props.traceId} />
                    }
                })()}
            </div>
        )
    }

    private hasTraceHierachyWithChildren(): boolean {
        return false
    }

    private setRoutingDefaultParameters() {
        this.props.router.setDefaultParameter('base', this.props.match.params.profile)
        this.props.router.setDefaultParameter('profile', this.props.match.params.profile)
    }
}

export default connect<StateProps, OwnProps>((globalState: ApplicationState, props: RouteComponentProps<MatchParameters> & OwnProps) => {
    const traceOld = globalState.traces.traces[props.traceId] || new Entity()

    return {
        tidewaysContext: globalState.tidewaysContext,
        router: globalState.tidewaysContext.router,
        route: globalState.tidewaysContext.route,
        trace: getTrace(globalState, props.traceId),
        traceId: props.traceId,
        traceLoaded: isTraceLoaded(globalState, props.traceId),
        ...traceOld,
    }
})(PublicTraceComponent)
